(() => {
  const tag = 'limite-1-unidade';
  const originalFetch = window.fetch.bind(window);
  const productCache = new Map();
  const variantToProduct = new Map();
  const limitedProducts = new Set();
  let addQueue = Promise.resolve();
  let refreshQueued = false;

  const same = (a, b) => String(a ?? '') === String(b ?? '');
  const isNested = (line) => Boolean(
    line.parent_relationship &&
    (line.parent_relationship.parent || line.parent_relationship.parent_key)
  );
  const root = () => window.Shopify?.routes?.root || '/';
  const message = () =>
    (window.quickOrderListStrings?.max_error || 'Maximum quantity: [max]')
      .replace('[max]', '1');

  async function getCart() {
    const response = await originalFetch(`${root()}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('cart_unavailable');
    return response.json();
  }

  async function productHasLimit(handle) {
    if (!handle) return false;
    if (!productCache.has(handle)) {
      const result = originalFetch(`${root()}products/${encodeURIComponent(handle)}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
        .then((response) => {
          if (!response.ok) throw new Error('product_unavailable');
          return response.json();
        })
        .then((product) => {
          const tags = Array.isArray(product.tags)
            ? product.tags
            : String(product.tags || '').split(',');
          return tags.some((value) => value.trim().toLowerCase() === tag);
        });
      productCache.set(handle, result);
    }
    try {
      return await productCache.get(handle);
    } catch {
      productCache.delete(handle);
      return false;
    }
  }

  function registerLimitedProducts() {
    document.querySelectorAll('[data-limit-one-product-id][data-limit-one-variant-ids]').forEach((element) => {
      const productId = String(element.dataset.limitOneProductId);
      limitedProducts.add(productId);
      String(element.dataset.limitOneVariantIds || '').split(',').forEach((variantId) => {
        if (variantId.trim()) variantToProduct.set(variantId.trim(), productId);
      });
    });
  }

  async function limitedProductId(item) {
    if (!item || isNested(item)) return null;
    const id = String(item.product_id);
    if (limitedProducts.has(id) || await productHasLimit(item.handle)) return id;
    return null;
  }

  function parseBody(options) {
    const body = options?.body;
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const object = {};
      for (const [key, value] of body.entries()) object[key] = value;
      if (object.id) return { items: [{ id: object.id, quantity: Number(object.quantity || 1) }] };
      const items = [];
      for (const [key, value] of body.entries()) {
        const match = key.match(/^items\[(\d+)\]\[id\]$/);
        if (match) {
          items.push({ id: value, quantity: Number(body.get(`items[${match[1]}][quantity]`) || 1) });
        }
      }
      return { items };
    }
    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch {
        const params = new URLSearchParams(body);
        return { items: [{ id: params.get('id'), quantity: Number(params.get('quantity') || 1) }] };
      }
    }
    return null;
  }

  function block() {
    const text = message();
    document.dispatchEvent(new CustomEvent('limited-product:blocked', { detail: { message: text } }));
    return new Response(JSON.stringify({ status: 422, message: text, description: text }), {
      status: 422,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  document.addEventListener('limited-product:blocked', (event) => {
    let banner = document.getElementById('limited-product-notice');
    if (!banner) {
      banner = document.createElement('div');
      banner.id = 'limited-product-notice';
      banner.setAttribute('role', 'status');
      banner.style.cssText = 'position:fixed;bottom:18px;left:18px;right:18px;max-width:480px;margin:auto;padding:14px 18px;border-radius:8px;background:#222;color:#fff;z-index:99999;text-align:center;box-shadow:0 3px 16px #0003';
      document.body.appendChild(banner);
    }
    banner.textContent = event.detail.message;
    clearTimeout(banner.hideTimer);
    banner.hideTimer = setTimeout(() => banner.remove(), 4000);
  });

  function cartAction(url) {
    try {
      const parsed = new URL(url instanceof Request ? url.url : url, location.href);
      if (parsed.origin !== location.origin) return null;
      return parsed.pathname.match(/(?:^|\/)cart\/(add|change|update)(?:\.js)?\/?$/)?.[1] || null;
    } catch {
      return null;
    }
  }

  async function shouldBlockAdd(items) {
    const cart = await getCart();
    const directLines = (cart.items || []).filter((item) => !isNested(item));
    const incomingByProduct = new Map();

    for (const item of items) {
      const variantId = String(item.id);
      const existing = directLines.find((line) => same(line.variant_id ?? line.id, variantId));
      let productId = variantToProduct.get(variantId);
      if (!productId && existing) productId = await limitedProductId(existing);
      if (!productId) continue;
      const quantity = Number(item.quantity ?? 1);
      incomingByProduct.set(productId, (incomingByProduct.get(productId) || 0) + quantity);
    }

    for (const [productId, quantity] of incomingByProduct) {
      const current = directLines.reduce((total, line) =>
        total + (same(line.product_id, productId) ? Number(line.quantity || 0) : 0), 0);
      if (current + quantity > 1) return true;
    }
    return false;
  }

  async function shouldBlockChange(payload) {
    if (Number(payload.quantity) <= 1) return false;
    const cart = await getCart();
    const item = payload.line
      ? cart.items?.[Number(payload.line) - 1]
      : cart.items?.find((line) => same(line.key, payload.id) || same(line.variant_id, payload.id));
    return Boolean(await limitedProductId(item));
  }

  async function shouldBlockUpdate(payload) {
    if (!payload.updates) return false;
    const cart = await getCart();
    const entries = Object.entries(payload.updates);
    for (const [key, quantity] of entries) {
      if (Number(quantity) <= 1) continue;
      const item = cart.items?.find((line) => same(line.key, key) || same(line.variant_id, key));
      if (await limitedProductId(item)) return true;
      if (limitedProducts.has(variantToProduct.get(String(key)))) return true;
    }
    return false;
  }

  window.fetch = function limitedProductFetch(input, options) {
    const action = cartAction(input);
    if (!action || !options?.body) return originalFetch(input, options);
    const payload = parseBody(options);
    if (!payload) return originalFetch(input, options);

    if (action === 'add') {
      const items = payload.items || (payload.id ? [payload] : []);
      const execute = async () => {
        try {
          if (await shouldBlockAdd(items)) return block();
        } catch {
          return block();
        }
        return originalFetch(input, options);
      };
      const next = addQueue.then(execute, execute);
      addQueue = next.then(() => undefined, () => undefined);
      return next;
    }

    if (action === 'change') {
      return shouldBlockChange(payload).then((blocked) => blocked ? block() : originalFetch(input, options));
    }

    if (action === 'update') {
      return shouldBlockUpdate(payload).then((blocked) => blocked ? block() : originalFetch(input, options));
    }

    return originalFetch(input, options);
  };

  function limitedQuantityInput(input) {
    return Boolean(input.closest('quantity-input[data-limit-one-product-id]'));
  }

  function refreshQuantityControls() {
    refreshQueued = false;
    registerLimitedProducts();
    document.querySelectorAll('quantity-input[data-limit-one-product-id] input.quantity__input').forEach((input) => {
      if (!limitedQuantityInput(input)) return;
      input.max = '1';
      input.dataset.max = '1';
      if (Number(input.value) > 1) {
        input.value = '1';
        if (input.closest('cart-items, cart-drawer-items')) {
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      const plus = input.closest('quantity-input')?.querySelector('[name="plus"]');
      if (plus) plus.disabled = Number(input.value) >= 1;
    });
  }

  function queueRefresh() {
    if (refreshQueued) return;
    refreshQueued = true;
    requestAnimationFrame(refreshQuantityControls);
  }

  document.addEventListener('click', (event) => {
    const plus = event.target.closest('quantity-input [name="plus"]');
    const input = plus?.closest('quantity-input')?.querySelector('input.quantity__input');
    if (input && limitedQuantityInput(input) && Number(input.value) >= 1) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  document.addEventListener('change', (event) => {
    if (event.target.matches('input.quantity__input')) queueRefresh();
  }, true);

  const init = () => {
    queueRefresh();
    new MutationObserver(queueRefresh).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
